import { useState, useEffect } from 'react';
import { Alert } from 'react-bootstrap';
import { useMediaQuery } from 'react-responsive';
import {
  ComposedChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { buildElevationData } from './RouteElevationChart';
import { RouteAnalysisResponse, StrategyOptions, EnergyProfilePoint } from '../../models';
import { calculateEnergyProfile, checkBonkRisk } from '../../api/Api';
import { useLocaleContext } from '../../i18n';

interface FuelPoint {
  km: number;
  carbs: number;
}

interface Props {
  route: RouteAnalysisResponse;
  strategyOptions: StrategyOptions;
  fuelPoints: FuelPoint[];
  height?: number;
  cursor?: string;
  onClick?: (data: any) => void;
  highlightedKm?: number | null;
}

export default function RouteEnergyChart({
  route,
  strategyOptions,
  fuelPoints,
  height = 240,
  cursor,
  onClick,
  highlightedKm,
}: Props) {
  const { locale } = useLocaleContext();
  const isDesktop = useMediaQuery({ minWidth: 768 });
  const [energyProfile, setEnergyProfile] = useState<EnergyProfilePoint[]>([]);
  const [warningKm, setWarningKm] = useState<number | null>(null);
  const [bonkKm, setBonkKm] = useState<number | null>(null);
  const [bonkWarning, setBonkWarning] = useState<string | null>(null);

  const fuelPointsKey = JSON.stringify(fuelPoints);

  useEffect(() => {
    let cancelled = false;
    const req = {
      route,
      strategyOptions,
      fuelIntervals: fuelPoints.map(p => ({ kilometerMark: p.km, targetCarbs: p.carbs })),
    };

    const timer = setTimeout(async () => {
      if (cancelled) return;
      try {
        const [profile, bonk] = await Promise.all([
          calculateEnergyProfile(req),
          checkBonkRisk(req),
        ]);
        if (!cancelled) {
          setEnergyProfile(profile.profile);
          setWarningKm(bonk.warningKm ?? null);
          setBonkKm(bonk.bonkKm ?? null);
          setBonkWarning(bonk.warning ?? null);
        }
      } catch {}
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [fuelPointsKey]); // eslint-disable-line

  const elevationData = buildElevationData(route.segments);

  const mergedData = energyProfile.length > 0
    ? elevationData.map(elev => {
        const energy = energyProfile.find(e => Math.abs(e.km - elev.km) < 0.01);
        return { ...elev, energyGrams: energy?.energyGrams ?? 0 };
      })
    : elevationData;

  const getLabelPosition = (index: number) => {
    if (fuelPoints.length <= 1) return 'top';
    const current = fuelPoints[index];
    const prev = index > 0 ? fuelPoints[index - 1] : null;
    const next = index < fuelPoints.length - 1 ? fuelPoints[index + 1] : null;

    const MIN_KM_DISTANCE = 3;
    const tooCloseToNeighbor = (prev && current.km - prev.km < MIN_KM_DISTANCE) || (next && next.km - current.km < MIN_KM_DISTANCE);

    if (!tooCloseToNeighbor) return 'top';

    if (prev && current.km - prev.km < MIN_KM_DISTANCE) return 'bottom';
    if (next && next.km - current.km < MIN_KM_DISTANCE) return index % 2 === 0 ? 'top' : 'bottom';

    return 'top';
  };

  return (
    <>
      <div style={{ width: '100%', height, cursor, position: 'relative' }}>
        <div className="d-none d-md-block">
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'none',
              fontSize: '0.95rem',
              color: 'rgba(255, 255, 255, 0.3)',
              textAlign: 'center',
              zIndex: 0,
            }}
          >
            {locale.planner.chartHint}
          </div>
        </div>
        <ResponsiveContainer>
          <ComposedChart
            data={mergedData}
            onClick={isDesktop ? onClick : undefined}
            margin={{ top: 40, right: 50, left: 10, bottom: 24 }}
          >
            <defs>
              <linearGradient id="elevGradientEnergy" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#198754" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#198754" stopOpacity={0.01} />
              </linearGradient>
              <linearGradient id="nutritionGradientEnergy" x1="0" y1="0" x2="1" y2="0">
                {warningKm != null ? (() => {
                  const wPct = `${(warningKm / route.totalDistanceKm * 100).toFixed(1)}%`;
                  if (bonkKm != null) {
                    const bPct = `${(bonkKm / route.totalDistanceKm * 100).toFixed(1)}%`;
                    return <>
                      <stop offset={wPct} stopColor="#00ffff" stopOpacity={0.4} />
                      <stop offset={wPct} stopColor="#fd7e14" stopOpacity={0.4} />
                      <stop offset={bPct} stopColor="#fd7e14" stopOpacity={0.35} />
                      <stop offset={bPct} stopColor="#dc3545" stopOpacity={0.45} />
                      <stop offset="100%" stopColor="#dc3545" stopOpacity={0.1} />
                    </>;
                  }
                  return <>
                    <stop offset={wPct} stopColor="#00ffff" stopOpacity={0.4} />
                    <stop offset={wPct} stopColor="#fd7e14" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#fd7e14" stopOpacity={0.1} />
                  </>;
                })() : <>
                  <stop offset="0%" stopColor="#00ffff" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#00ffff" stopOpacity={0.1} />
                </>}
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#444" />
            <XAxis
              dataKey="km"
              type="number"
              domain={['dataMin', 'dataMax']}
              tickFormatter={(v) => v.toFixed(1)}
              label={{ value: 'Distance (km)', position: 'insideBottom', offset: -12, fill: '#adb5bd' }}
              tick={{ fill: '#adb5bd', fontSize: 11 }}
              stroke="#555"
            />
            <YAxis
              tickFormatter={(v) => `${v}`}
              tick={{ fill: '#adb5bd', fontSize: 11 }}
              stroke="#555"
              width={45}
              label={{ value: 'Elevation (m)', position: 'insideLeft', angle: -90, offset: 10, fill: '#adb5bd', fontSize: 12 }}
            />
            {energyProfile.length > 0 && (
              <YAxis
                yAxisId="nutrition"
                orientation="right"
                domain={[0, (energyProfile[0]?.energyGrams ?? 0) * 4 / 3]}
                tickFormatter={(v) => `${v}`}
                tick={{ fill: '#adb5bd', fontSize: 11 }}
                stroke="#555"
                width={45}
                label={{ value: 'Glycogen (g)', position: 'insideRight', angle: 90, offset: 10, fill: '#adb5bd', fontSize: 12 }}
              />
            )}
            <Tooltip
              contentStyle={{ backgroundColor: '#2b3035', border: '1px solid #495057', color: '#f8f9fa' }}
              formatter={(v: number, name: string) =>
                name === 'energyGrams'
                  ? [`${Math.round(v)} g`, 'Glycogen']
                  : [`${v} m`, 'Elevation']
              }
              labelFormatter={(km) => `${Number(km).toFixed(1)} km`}
            />
            <Area
              type="monotone"
              dataKey="elevation"
              stroke="#198754"
              strokeWidth={2}
              fill="url(#elevGradientEnergy)"
              dot={false}
              activeDot={{ r: 4, fill: '#198754' }}
            />
            {energyProfile.length > 0 && (
              <Area
                yAxisId="nutrition"
                dataKey="energyGrams"
                type="linear"
                stroke="#00ffff"
                strokeWidth={2.5}
                fill="url(#nutritionGradientEnergy)"
                dot={false}
                activeDot={false}
                isAnimationActive={false}
              />
            )}
            {fuelPoints.map((p, i) => (
              <ReferenceLine
                key={`${i}-${p.km}`}
                x={p.km}
                stroke={highlightedKm === p.km ? '#fff' : '#fd7e14'}
                strokeWidth={highlightedKm === p.km ? 3 : 2}
                strokeDasharray={highlightedKm === p.km ? undefined : '4 2'}
                label={{ value: `${p.carbs}g`, position: getLabelPosition(i), fill: '#fd7e14', fontSize: 10 }}
              />
            ))}
            {warningKm != null && (
              <ReferenceLine
                x={warningKm}
                stroke="#dc3545"
                strokeWidth={2}
                strokeDasharray="4 2"
                label={(props: any) => (
                  <text x={props.viewBox.x} y={props.viewBox.y - 18} fill="#dc3545" fontSize={11} textAnchor="middle">⚠ Low</text>
                )}
              />
            )}
            {bonkKm != null && (
              <ReferenceLine
                x={bonkKm}
                stroke="#dc3545"
                strokeWidth={2}
                label={(props: any) => (
                  <text x={props.viewBox.x} y={props.viewBox.y - 18} fill="#dc3545" fontSize={11} textAnchor="middle">💀 Bonk</text>
                )}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {bonkWarning && <Alert variant="danger" className="mb-0 mt-2">{bonkWarning}</Alert>}
    </>
  );
}
